import parse from "html-react-parser";
import { splitExerciseSteps, parsePrescription } from "@sophy/shared-ui";

/**
 * Data shaping for SharedExerciseCard. Splitting is shared with the therapist
 * app, the HTML parsing stays here because the card takes ready-made nodes.
 */

/**
 * Steps as renderable nodes.
 *
 * @param {string|string[]} value - array from the program endpoint or one
 *   HTML string with <br /> separators
 * @returns {React.ReactNode[]}
 */
export function parseExerciseSteps(value) {
  return splitExerciseSteps(value).map((step) =>
    typeof step === "string" ? parse(step) : step
  );
}

/**
 * Turns the backend's "3 | 60 | 3" into labelled figures; returns null when
 * the format is different so the caller can fall back to the raw string.
 *
 * @param {string} prescription
 * @param {Function} t - i18next translate
 */
export function getPrescriptionItems(prescription, t) {
  const labels = {
    reps: t("exercise.reps"),
    pause: t("exercise.pause"),
    sets: t("exercise.sets"),
    secondsUnit: t("exercise.seconds"),
  };

  const parsed = parsePrescription(prescription, labels);
  if (parsed) return parsed;

  /* The program endpoint spells the prescription out — "12 WH, 60s Pause,
     3 Serien" — where the public one sends "12 | 60 | 3". Both put the same
     three figures in the same order, so read the numbers rather than the
     wording, which is the server's and translated. Anything that is not
     exactly three figures falls through to the raw string, unlabelled, rather
     than risk labelling the wrong ones. */
  const figures = String(prescription || "").match(/\d+(?:\s*[-–]\s*\d+)?/g);
  if (!figures || figures.length !== 3) return null;

  const [reps, pause, sets] = figures.map((figure) => figure.replace(/\s+/g, ""));
  return [
    { label: labels.reps, value: reps },
    { label: labels.pause, value: pause, unit: labels.secondsUnit },
    { label: labels.sets, value: sets },
  ];
}

/**
 * Control labels for SharedVideoPlayer — the shared package carries no i18n.
 *
 * @param {Function} t - i18next translate
 */
export function getPlayerLabels(t) {
  return {
    play: t("exercise.player.play"),
    pause: t("exercise.player.pause"),
    seek: t("exercise.player.seek"),
    slowMotion: t("exercise.player.slowMotion"),
    soundOn: t("exercise.player.soundOn"),
    soundOff: t("exercise.player.soundOff"),
    fullscreen: t("exercise.player.fullscreen"),
  };
}

/** Body regions are stored comma separated; one chip per region. */
export function getBodyRegionTags(value) {
  if (!value || typeof value !== "string") return [];
  return value
    .split(",")
    .map((region) => region.trim())
    .filter(Boolean);
}
